import { useCallback, useState, useRef, useEffect } from 'react';
import { ListItem, ListItemText, List } from '@mui/material';
import { CallStartOverlay } from 'components';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from 'store';
import { removePatient, selectPatients } from 'store/patientQueue';
import { selectUser } from 'store/user';
import { createRoom, getRoomHistory } from 'store/call';
import { useDialog, useSignalrConnection, useVideo, useChat, useEMDR } from 'hooks';
import { IEMDRContext } from 'context';
import { HubPatient } from 'types';
import { getFullName, stringifyIdentity } from 'utils';
import { RequestPaymentDialog } from 'components';
import { StatusDialog, PatientListItem, ContactDialog } from './components';
import { RemoveDialog, SimpleInfoDialog } from '../Dialogs';
import { selectCall } from 'store/call';
import { isHipaaLink } from '../../config/productProfile';

import { TransferDialog } from '../Dialogs/TransferDialog'; // Import dialog
import { TeleconsentWizard } from 'components/Teleconsent';
import { getAllFormKeys } from 'components/Teleconsent/forms/dynamicRegistry';
import { FORM_REGISTRY } from 'components/Teleconsent/forms/registry';
import { useTeleconsent } from 'hooks';

import styles from './PatientList.module.scss';

export enum PatientListDialogType {
  TRANSFER,
  STATUS,
  RENAME,
  CONTACT,
  PAYMENT,
  REMOVE,
  TELECONSENT,
}

export function PatientList({
  isCallStarted = false,
  isListPinned = false,
  onCallStartingChange,
  onAfterStartCall,
  onAfterOpenChat,
}: {
  isCallStarted?: boolean;
  isListPinned?: boolean;
  onCallStartingChange?: (isStarting: boolean) => void;
  onAfterStartCall?: () => void;
  onAfterOpenChat?: () => void;
}) {
  const dispatch = useAppDispatch();
  const patients = useAppSelector(selectPatients);
  const user = useAppSelector(selectUser);
  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation('providerRoom');
  const isRtl = i18n.dir() === 'rtl';
  const { connect: videoConnect, isConnecting, room } = useVideo();
  const { addPatientToRoomChat } = useChat();
  const { getToken, invitePatient, notifyCallPreparing, removePatientFromQueue } = useSignalrConnection();
  const { showDialog } = useDialog();
  const { state }: IEMDRContext = useEMDR();
  const { isOpen: isTeleconsentOpen, startSession: startTeleconsentSession } = useTeleconsent();

  const [loadingPatientId, setLoadingPatientId] = useState<string | null>(null);
  const [loadingPatientName, setLoadingPatientName] = useState<string | null>(null);
  const hasShownGroupRecommendationRef = useRef(false);

  const call = useAppSelector(selectCall);
  const isGroupRoom = call?.isGroup === true;

  useEffect(() => {
    hasShownGroupRecommendationRef.current = false;
  }, [room?.name]);

  const [loading, setLoading] = useState<boolean>(false);
  const isCallStarting = isConnecting || loading;

  useEffect(() => {
    onCallStartingChange?.(isCallStarting);
  }, [isCallStarting, onCallStartingChange]);

  const onStartCall = useCallback(
    (id: string) => {
      if (!user || state !== 'off') return;

      const patient = patients.find((p) => p.connectionId === id);
      setLoadingPatientName(patient?.userName ?? null);
      setLoading(true);

      notifyCallPreparing(id);

      const identity = stringifyIdentity([user.clinicUser.id, getFullName(user)]);

      // 🔑 LocalStorage-driven switch
      const forceGroup = localStorage.getItem('groupCallsEnabled') === 'true';

      dispatch(createRoom({ forceGroup }))
        .unwrap()
        .then(async (roomHistory) => {
          const roomId = roomHistory.room!;
          if (!roomId) {
            throw new Error('CreateRoom returned no room');
          }

          // Sync redux call state
          await dispatch(getRoomHistory()).unwrap();

          // Fetch token and enforce a 5-second minimum overlay before either side joins
          const [token] = await Promise.all([
            getToken({ identity, roomId }),
            new Promise<void>((resolve) => setTimeout(resolve, 5000)),
          ]);

          // Connect provider to room
          await videoConnect(token);

          // Invite patient AFTER provider is connected — joinCall fires here,
          // so patient also waits the full 5 seconds before connecting
          invitePatient({ connectionId: id, roomId });

          // Remove patient from queue
          dispatch(removePatient({ connectionId: id }));
        })
        .catch((e) => {
          console.error('[onStartCall] failed', e);
        })
        .finally(() => {
          setLoading(false);
          setLoadingPatientId(null);
          setLoadingPatientName(null);
        });
    },
    [user, state, patients, dispatch, getToken, videoConnect, invitePatient, notifyCallPreparing],
  );

  const onInvitePatient = useCallback(
    (id: string) => {
      if (!user) return;

      const participantCount = room?.participants?.size || 0;

      // 🚫 Hard cap (existing behavior)
      if (participantCount >= 15) {
        enqueueSnackbar(t('maxParticipationReached'), { variant: 'info' });
        return;
      }

      // 🚫 Existing EMDR block (unchanged)
      if (state !== 'off') {
        const modal = showDialog(SimpleInfoDialog, {
          text: t('pleaseCloseEmdrForParticipants'),
          onConfirm: () => {
            modal.destroy();
          },
        });
        return;
      }

      // ℹ️ Soft warning — ONLY for P2P rooms
      if (!isGroupRoom && participantCount >= 2 && !hasShownGroupRecommendationRef.current) {
        hasShownGroupRecommendationRef.current = true;

        const modal = showDialog(SimpleInfoDialog, {
          title: t('callLimitedTitle'),
          text: t('callLimitedText'),
          onConfirm: () => {
            modal.destroy();
          },
        });

        return;
      }

      // ✅ Always proceed with invite
      invitePatient({ connectionId: id, roomId: room?.name || '' });
      addPatientToRoomChat(id, room?.name || '');
      dispatch(removePatient({ connectionId: id }));
      setLoadingPatientId(null);
    },
    [user, room, state, invitePatient, addPatientToRoomChat, dispatch, enqueueSnackbar, showDialog, t],
  );

  const onDialogOpen = useCallback(
    (type: PatientListDialogType, patient: HubPatient) => {
      switch (type) {
        case PatientListDialogType.TRANSFER: {
          const modal = showDialog(TransferDialog, {
            patient,
            onClose: () => modal.destroy(),
          }) as { destroy: () => void };
          break;
        }
        case PatientListDialogType.STATUS: {
          const modal = showDialog(StatusDialog, {
            patient,
            onConfirm: () => {
              modal.destroy();
            },
          });
          break;
        }
        case PatientListDialogType.CONTACT: {
          const modal = showDialog(ContactDialog, {
            patient,
            onConfirm: () => {
              modal.destroy();
            },
          });
          break;
        }
        case PatientListDialogType.PAYMENT: {
          const modal = showDialog(RequestPaymentDialog, {
            clientId: patient.connectionId,
            clientName: patient.userName,
            onConfirm: () => {
              modal.destroy();
            },
          });
          break;
        }
        case PatientListDialogType.REMOVE: {
          const modal = showDialog(RemoveDialog, {
            userName: patient.userName,
            onConfirm: () => {
              modal.destroy();
              removePatientFromQueue(patient.connectionId);
            },
          });
          break;
        }
        case PatientListDialogType.TELECONSENT: {
          const disabledSystemFormKeys = user?.clinicUser.teleconsentDisabledForms ?? [];
          const systemFormKeys = Object.keys(FORM_REGISTRY);
          const allFormKeys = getAllFormKeys().filter(
            (k) => !systemFormKeys.includes(k) || !disabledSystemFormKeys.includes(k),
          );
          const modal: { destroy: () => void; update: (p: any) => void } = showDialog(TeleconsentWizard, {
            participants: null,
            enabledFormKeys: allFormKeys,
            onBegin: async (
              formKey: string,
              _participantIdentity: string | null,
              providerPrefill?: Record<string, string>,
              _participantDisplayName?: string,
              providerName?: string,
              clientLabel?: string,
            ) => {
              modal.destroy();
              await startTeleconsentSession(
                formKey,
                patient.connectionId,
                providerPrefill,
                patient.userName,
                providerName,
                clientLabel,
              );
            },
            onCancel: () => modal.destroy(),
          });
          break;
        }
        default:
          break;
      }
    },
    [user, isTeleconsentOpen, startTeleconsentSession],
  );

  return (
    <>
      {loadingPatientName && <CallStartOverlay message={t('callOverlay.starting', { name: loadingPatientName })} />}
      <List sx={{ width: '100%', bgcolor: 'transparent' }} className={styles.patientList}>
        {[...patients]
          .sort(
            (patientA: HubPatient, patientB: HubPatient) =>
              new Date(patientA.joinedOn).getTime() - new Date(patientB.joinedOn).getTime(),
          )
          .map((patient) => (
            <PatientListItem
              key={patient.connectionId}
              isCallStarted={isCallStarted}
              isListPinned={isListPinned}
              patient={patient}
              onStartCall={isCallStarted ? onInvitePatient : onStartCall}
              onAfterStartCall={onAfterStartCall}
              onAfterOpenChat={onAfterOpenChat}
              onDialogOpen={(type) => onDialogOpen(type, patient)}
              loadingPatientId={loadingPatientId}
              setLoadingPatientId={setLoadingPatientId}
            />
          ))}

        {patients.length === 0 && (
          <ListItem>
            <ListItemText
              primary={isHipaaLink ? t('noClientsInQueue') : t('noGuestsInQueue')}
              primaryTypographyProps={{ sx: { textAlign: isRtl ? 'right' : 'left' } }}
            />
          </ListItem>
        )}
      </List>
    </>
  );
}
